import React, { useState, FormEvent, useEffect, useMemo } from "react";

import classNames from "classnames";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";

import { ApiStatus } from "~/api/fetchStudioApi";
import * as productStyles from "~/components/videoProductPage/Product.css";
import { useCreatorEditing } from "~/hooks/useCreatorEditing";

import * as styles from "./WhoIsThisVideoFor.css";
import { Props, STYLE_TYPES } from "./WhoIsThisVideoForInterface";

const allStyles = {
  [STYLE_TYPES.REGULAR]: {
    videoForSingleItemBlockAdditional: styles.videoForSingleItemBlockRegular,
    videoForSingleItemBlockLogoAdditional: styles.videoForSingleItemBlockLogoRegular,
  },
  [STYLE_TYPES.LIGHT]: {
    videoForSingleItemBlockAdditional: styles.videoForSingleItemBlockLight,
    videoForSingleItemBlockLogoAdditional: styles.videoForSingleItemBlockLogoLight,
  },
};

export const WhoIsThisVideoFor = ({
  productData,
  isInEditMode,
  indexBackgroundGlow,
  itemType = STYLE_TYPES.REGULAR,
}: Props): React.ReactElement => {
  const descriptor = "class";
  //productData && productData.content && productData.content.length > 1 ? "series" : "video";

  const additionalStyles = useMemo(() => {
    return allStyles[itemType as keyof typeof STYLE_TYPES];
  }, []);

  const {
    saveProductDraftUpdate,
    CHANGES_SAVING_TEXT,
    CHANGES_SAVED_TEXT,
    CHANGES_ERROR_TEXT,
    CHANGES_LIMIT_TEXT,
    setIsSavingChanges,
    setSavingStatusText,
  } = useCreatorEditing();
  const [whosThisForItems, setWhosThisForItems] = useState(productData.whoIsThisForSection);
  const WHOS_THIS_FOR_MAX_LENGTH = 500;

  useEffect(() => {
    setWhosThisForItems(productData.whoIsThisForSection);
  }, [productData]);

  const handleItemChange = (event: FormEvent<HTMLDivElement>, itemIndex: number) => {
    let newText = (event.target as HTMLDivElement).textContent || "";
    const newWhosThisForItems = whosThisForItems.map((st, index) => {
      return index === itemIndex ? newText : st;
    });
    setWhosThisForItems(newWhosThisForItems);
    setIsSavingChanges(true);
    setSavingStatusText(CHANGES_SAVING_TEXT);
    saveProductDraftUpdate(productData, false, "whoIsThisForSection", newWhosThisForItems).then(
      (res: { status: ApiStatus }) => {
        setSavingStatusText(
          res.status == ApiStatus.SUCCESS ? CHANGES_SAVED_TEXT : CHANGES_ERROR_TEXT,
        );

        setIsSavingChanges(false);
      },
    );
  };

  // This is called every time there's a character change.
  const handleItemEdit = (event: FormEvent<HTMLDivElement>, itemIndex: number) => {
    let newText = (event.target as HTMLDivElement).textContent || "";



    setSavingStatusText(CHANGES_SAVING_TEXT);
    if (newText.length > WHOS_THIS_FOR_MAX_LENGTH) {
      //setIsTitleOnError(true);
      setSavingStatusText(CHANGES_LIMIT_TEXT);
      event.preventDefault();
      return;
    } else {
      setIsSavingChanges(true);
      //setIsTitleOnError(false);
      const newWhosThisForItems = whosThisForItems.map((string, index) => {
        if (index === itemIndex) {
          return newText;
        } else {
          return string;
        }
      });

      saveProductDraftUpdate(productData, true, "whoIsThisForSection", newWhosThisForItems).then(
        (res: { status: ApiStatus }) => {
          setSavingStatusText(
            res.status == ApiStatus.SUCCESS ? CHANGES_SAVED_TEXT : CHANGES_ERROR_TEXT,
          );

          setIsSavingChanges(false);
        },
      );
    }
  };

  const addBoldTextInEditMode = (el: string): Array<string | JSX.Element> => {
    const formattedTextArray: Array<string | JSX.Element> = [];
    const regex = /\*\*(.*?)\*\*/g;
    let match;
    let currentIndex = 0;

    while ((match = regex.exec(el)) !== null) {
      const normalText = el.substring(currentIndex, match.index);
      if (normalText.trim().length > 0) {
        formattedTextArray.push(<span key={`p-${currentIndex}`}>{normalText}</span>);
      }
      const boldText = match[1];
      formattedTextArray.push(
        <span key={`span-${currentIndex}`} style={{ fontWeight: "100" }}>
          **
          <span style={{ fontWeight: "bold" }}>{boldText}</span>
          **
        </span>,
      );
      currentIndex = match.index + match[0].length;
    }

    const remainingText = el.substring(currentIndex);
    if (remainingText.trim().length > 0) {
      formattedTextArray.push(<span key={`p-${currentIndex}`}>{remainingText}</span>);
    }

    return formattedTextArray;
  };

  return (
    <section className={styles.videoForSection}>
      <div className={styles.videoForTitle}>{"Who’s this " + descriptor + " for?"}</div>
      <div className={styles.videoForItemsBlock}>
        {whosThisForItems.map((el, i) => (
          <div
            key={el + i.toString()}
            className={classNames(
              indexBackgroundGlow && styles.grayBorderColor,
              styles.videoForSingleItemBlock,
              additionalStyles.videoForSingleItemBlockAdditional,
            )}
          >
            <div className={styles.videoForSingleItemBlockLogoContainer}>
              {indexBackgroundGlow && <div className={styles.gradientUnderlay} />}
              <div
                className={classNames(
                  indexBackgroundGlow && styles.lightVideoForSingleItemBlockLogo,
                  styles.videoForSingleItemBlockLogo,
                  additionalStyles.videoForSingleItemBlockLogoAdditional,
                )}
              >
                {i + 1}
              </div>
            </div>
            <div className={styles.videoForSingleItemBlockText}>
              <div
                className={classNames(isInEditMode && productStyles.editableElement)}
                contentEditable={isInEditMode}
                onInput={(e) => handleItemEdit(e, i)}
                onBlur={(e) => handleItemChange(e, i)}
                suppressContentEditableWarning={true}
              >
                {!isInEditMode ? <ReactMarkdown>{el}</ReactMarkdown> : addBoldTextInEditMode(el)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
